import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';

const Home = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/workflow');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 leading-tight">
              Primer Probe Assay
              <br />
              <span className="gradient-text">Design Request Hub</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Professional custom-designed primer and probe assays for molecular diagnostics and research.
              Configure, price, and order specialized PCR assays through our guided platform. You may choose to 
              purchase only the design service, or alternatively, you are free to use it within your own designs 
              at no cost.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="text-lg px-8 py-6 bg-blue-600 hover:bg-blue-700"
              onClick={handleGetStarted}
              data-testid="get-started-button"
            >
              Request Your Assay
            </Button>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
              data-testid="contact-link"
            >
              Or send us an email →
            </button>
          </div>

          <div className="pt-4">
            <a
              href="/MC-Assay-Design-Template.xlsx"
              download
              className="text-sm text-gray-600 hover:text-gray-900 underline"
              data-testid="sample-form-link"
            >
              Download Sample Design Request Form (Excel)
            </a>
          </div>
        </div>
      </section>

      {/* Features Section - Clickable PCR Ready Assay Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Ready-to-Use Assay Products</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Browse our catalog of pre-designed and validated detection kits, including pathogen detection assays.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <button
            onClick={() => navigate('/ready-assays', { state: { technology: 'realtime_pcr' } })}
            className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all text-left group cursor-pointer"
            data-testid="realtime-pcr-card"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">Real-Time PCR Ready Assays</h3>
            <p className="text-gray-600 leading-relaxed">
              Pre-validated detection kits for pathogens, gene expression, and more. 
              Ready-to-use primer and probe mixes for quantitative PCR applications.
            </p>
            <span className="inline-flex items-center text-blue-600 mt-4 text-sm font-medium group-hover:translate-x-1 transition-transform">
              Browse products →
            </span>
          </button>

          <button
            onClick={() => navigate('/ready-assays', { state: { technology: 'digital_pcr' } })}
            className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all text-left group cursor-pointer"
            data-testid="digital-pcr-card"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">Digital PCR Ready Assays</h3>
            <p className="text-gray-600 leading-relaxed">
              Pre-validated detection kits optimized for digital PCR platforms.
              Absolute quantification with superior precision.
            </p>
            <span className="inline-flex items-center text-blue-600 mt-4 text-sm font-medium group-hover:translate-x-1 transition-transform">
              Browse products →
            </span>
          </button>
        </div>
      </section>

      {/* Tools Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gray-50">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Design Tools</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Use our powerful design tools to create custom assays tailored to your specific research needs.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <button
            onClick={() => navigate('/probe-ready-design')}
            className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:border-purple-400 hover:shadow-lg transition-all text-left group cursor-pointer"
            data-testid="probe-ready-design-card"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">Probe Ready Assay Design</h3>
            <p className="text-gray-600 leading-relaxed">
              Design probe-based assays with our intuitive wizard. Input your target sequences 
              and get optimized primer and probe sets for your experiments.
            </p>
            <span className="inline-flex items-center text-purple-600 mt-4 text-sm font-medium group-hover:translate-x-1 transition-transform">
              Start designing →
            </span>
          </button>

          <button
            onClick={() => navigate('/primer-design-wizard')}
            className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:border-green-400 hover:shadow-lg transition-all text-left group cursor-pointer"
            data-testid="primer-design-wizard-card"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">PrimerDesignWizard</h3>
            <p className="text-gray-600 leading-relaxed">
              Advanced primer design tool based on Primer3. Design primers for PCR, qPCR, 
              and sequencing applications with customizable parameters.
            </p>
            <span className="inline-flex items-center text-green-600 mt-4 text-sm font-medium group-hover:translate-x-1 transition-transform">
              Start designing →
            </span>
          </button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-2">100+</div>
              <div className="text-blue-100">Targets per Assay</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">6-Plex</div>
              <div className="text-blue-100">Multiplex Capability</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-100">Online Ordering</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600">
          <p>© 2025 Primer Probe Assay Design Request Hub. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
